import { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { InteractiveElement } from '#src/atoms/animations/InteractiveElement/index.tsx';

const FoilContainer = styled.div`
  width: 100%;
  vertical-align: top;
  position: relative;
  display: inline-grid;
  mask-image: var(--mask-shape);
  mask-mode: alpha;
  mask-size: cover;
  mask-position: center;
  mask-repeat: no-repeat;
`;

const BACKGROUNDS = [
  {
    image: `var(--foil-color)`,
    blendMode: 'multiply',
    size: 'cover',
  },
  {
    image: `radial-gradient(
    75% 75% at
    calc(var(--mx, 0.5) * 100%) calc(var(--my, 0.5) * 100%) ,
    hsla(0, 0%, 0%, 0),
    hsla(0, 0%, 0%, 1)
  )`,
    blendMode: 'hard-light',
  },
  {
    image: `linear-gradient(
    var(--angle),
    #000 0%,
    #000 26%,
    #fff 28%,
    #fff 32%,
    #000 34%,
    #000 46%,
    #fff 48%,
    #fff 52%,
    #000 54%,
    #000 66%,
    #fff 68%,
    #fff 72%,
    #000 74%,
    #000 100%
  )`,
    blendMode: 'normal',
    attachment: 'local',
    size: '200% 200%',
    position: `
        calc(100% * (1 - var(--mx, 0)))
        calc(100% * (1 - var(--my, 0)))
        `,
  },
];

const FoilOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: calc(var(--active, 0) * 1);
  transition: opacity 100ms ease-in-out;
  mix-blend-mode: color-dodge;
  mask-image: var(--mask-foil);
  mask-mode: alpha;
  mask-size: cover;
  mask-position: center;
  mask-repeat: no-repeat;

  background-image: ${BACKGROUNDS.map(({ image }) => image).join(',')};
  background-blend-mode: ${BACKGROUNDS.map(
    ({ blendMode = 'normal' }) => blendMode
  ).join(',')};
  background-position: ${BACKGROUNDS.map(
    ({ position = 'center' }) => position
  ).join(',')};
  background-size: ${BACKGROUNDS.map(({ size = 'auto' }) => size).join(',')};
  background-repeat: ${BACKGROUNDS.map(() => 'repeat').join(',')};
  will-change: background-position, background-image;
`;

type FoilProps = {
  maskFoil: string;
  maskShape?: string;
  foilColor?: string;
  children?: React.ReactNode;
};

export const Foil = ({
  maskShape,
  maskFoil,
  foilColor,
  children,
}: FoilProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return () => {};

    const updateAngle = () => {
      const rect = el.getBoundingClientRect();
      el.style.setProperty(
        '--angle',
        `${-1 * Math.atan(rect.height / rect.width)}rad`
      );
    };

    updateAngle();
    const observer = new ResizeObserver(updateAngle);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <FoilContainer
      ref={containerRef}
      style={
        {
          '--mask-shape': maskShape
            ? `url(${maskShape})`
            : 'linear-gradient(0deg, #000,#000)',
          '--mask-foil': `url(${maskFoil})`,
          '--foil-color': foilColor
            ? `url(${foilColor})`
            : `repeating-linear-gradient(45deg, #ff0000 0%, #ff7f00 20px, #ffff00 40px, #00ff00 60px, #0000ff 80px, #4b0082 100px, #9400d3 120px, #ff0000 140px )`,
        } as React.CSSProperties
      }
    >
      <InteractiveElement>
        {children}
        <FoilOverlay />
      </InteractiveElement>
    </FoilContainer>
  );
};
